<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useVirtualKeyboard } from '@/composables/keyboard'

const { vkeyboard, keyboardType, canAutoHideOnEnter, activeTarget } = useVirtualKeyboard();

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: null
  },
  type: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: ""
  },
  enabled: {
    type: Boolean,
    default: true
  },
  required: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  validation: {
    type: String,
    default: null
  },
  rows: {
    type: [ String, Number ],
    default: null
  },
  modelValue: {
  }
});

const emit = defineEmits([ "update:modelValue", "change", "enter" ]);

const focus = (e) => { };
const unfocus = (e) => { };
defineExpose({ focus, unfocus });

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
    emit('change', value);
  }
});

const textarea = ref();
const textMirror = ref();

const isFocused = computed(() => activeTarget.value === textarea.value);

watch(isFocused, (focused) => {
  if (focused) {
    vkeyboard.on("enter", onEnterKey);
    canAutoHideOnEnter.value = false;
  }
  else {
    vkeyboard.removeListener("enter", onEnterKey);
    canAutoHideOnEnter.value = true;
  }
});

// watch(() => props.modelValue, () => {
//   updateMirrorLayer();
// });

onMounted(() => {
  initializeTextMirror();
  updateMirrorLayer();
});

onUnmounted(() => {
  vkeyboard.removeListener("enter", onEnterKey);
  disposeTextMirror();
});

function onFocus(e) {
  if (props.enabled && !props.readonly) {
    keyboardType.value = props.type == "numeric" ? "numeric" : "default";
    focus(e);
    vkeyboard.show(e.target);
  }
}

function onUnfocus(e) {
  if (props.enabled && !props.readonly) {
    unfocus(e);
    vkeyboard.hide(e.target, { delay: 120 }); //Give other input a chance to cancel hide within 30ms
  }
}

function onEnterKey() {
  //emit("enter");
  console.log("ENTER: TextArea add new line")
  value.value += "\n"; //Add new line
}

let caretNode = null;
let dragDownX = null;
let dragCurrentX = null;

function onPointerDown(e) {
  if (e.pointerType === "mouse" && e.button !== 0) return;
  dragDownX = e.clientX;
  dragCurrentX = e.clientX;
}

function onPointerMove(e) {
  if (dragDownX == null) return;
  dragCurrentX = e.clientX;
  updateMirrorLayer();
}

function onPointerEnd() {
  dragDownX = null;
  dragCurrentX = null;
  //Force one more mirror refresh so the caret can re-anchor (e.g., after a backward drag)
  if (textarea.value) updateMirrorLayer();
}
/*
function updateCaretPosition() {
  const input = textbox.value;
  const fullText = input.value;
  
  //Get the exact character index where the cursor currently lives
  const caretIndex = input.selectionStart;

  //Split the text at the cursor index
  const leftText = fullText.substring(0, caretIndex);
  const rightText = fullText.substring(caretIndex);

  //Use textContent to safely output text and preserve spaces
  textBefore.value.textContent = leftText;
  textAfter.value.textContent = rightText;
}
*/
function updateMirrorLayer() {
  const mirror = textMirror.value;
  const input = textarea.value;
  const fullText = input.value;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const direction = input.selectionDirection; //"forward" | "backward" | "none"
  const hasSelection = start !== end;
  const draggedBackward = dragDownX != null && dragCurrentX != null && dragCurrentX < dragDownX;
  const caretOnLeft = hasSelection && (direction === "backward" || draggedBackward);

  // Clear old elements from mirror
  mirror.textContent = '';

  // 1. Text Before Caret/Selection
  const leftText = fullText.substring(0, start);
  mirror.appendChild(document.createTextNode(leftText));

  // 2a. Caret on the left of the selection
  if (caretOnLeft) {
    mirror.appendChild(caretNode);
  }

  // 2b. Selection area
  if (hasSelection) {
    mirror.classList.add('has-selection');
    const selectedText = fullText.substring(start, end);
    const selectionSpan = document.createElement('span');
    selectionSpan.className = 'custom-selection';
    selectionSpan.textContent = selectedText;
    mirror.appendChild(selectionSpan);
  }
  else {
    mirror.classList.remove('has-selection');
  }

  // 3. Caret on the right of the selection
  if (!caretOnLeft) {
    mirror.appendChild(caretNode);
  }

  // 4. Text After Caret/Selection
  const rightText = fullText.substring(end);
  mirror.appendChild(document.createTextNode(rightText));

  // Keep scrolling synchronized between layers
  mirror.scrollTop = input.scrollTop;
}

function selectionChange() {
  //Only track selection changes if our input is currently active
  //if (document.activeElement === textarea.value) {
    //updateCaretPosition();
    updateMirrorLayer();
  //}
}

function initializeTextMirror() {
  const input = textarea.value;
  const mirror = textMirror.value;

  // Create caret element once
  caretNode = document.createElement('span');
  caretNode.className = 'custom-caret';
  const teardrop = document.createElement('span');
  teardrop.className = 'touch-teardrop';
  caretNode.appendChild(teardrop);

  // Handle typing, scrolling, and tapping
  input.addEventListener('input', updateMirrorLayer);
  input.addEventListener('scroll', () => {
    mirror.scrollTop = input.scrollTop;
  });

  document.addEventListener('selectionchange', selectionChange);
}

function disposeTextMirror() {
  textarea.value?.removeEventListener('input', updateMirrorLayer);
  document.removeEventListener('selectionchange', selectionChange);
}
</script>

<template>
  <div class="textarea-container">
    <textarea :id="props.id" ref="textarea" v-model="value" :placeholder="props.placeholder"
      :required="!!props.required" :readonly="!!props.readonly" :disabled="!props.enabled"
      role="presentation" :rows="props.rows" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
      class="textarea custom-textarea" :class="[ { 'textarea-focus focus-for-keyboard': isFocused, 'textarea-error': !!props.validation }, $attrs.class ]"
      @focus="onFocus" @blur="onUnfocus"
      @pointerdown="onPointerDown" @pointermove="onPointerMove" @pointerup="onPointerEnd" @pointercancel="onPointerEnd"></textarea>
      <!-- @keypress.enter="onEnterKey" -->

    <div class="text-mirror" ref="textMirror"></div>

    <div class="textbox-validation-message" :class="$attrs.class" v-if="!!props.validation">
      {{ typeof validation == "string" ? validation : validation.message }}
    </div>
  </div>
</template>

<style scoped>
.textarea {
  @apply bg-white border border-gray-300 text-gray-900 text-sm rounded-lg
    focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5
    dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400
    dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500;
}

.textbox:disabled {
  @apply text-gray-400;
}

/*
.textarea.textarea-focus {
  @apply ring-blue-500 border-blue-500 dark:ring-blue-500 dark:border-blue-500;
}
*/

.textarea.textarea-error {
  @apply border-red-700 bg-red-100 text-red-900 focus:ring-red-500 focus:border-red-500;
}

.textarea:focus,
.textarea.focus-for-keyboard {
  @apply ring-blue-500 border-blue-500;

  outline: 2px solid transparent;
  outline-offset: 2px;
  --tw-ring-inset: var(--tw-empty, );
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: #1C64F2;
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow);
  border-color: #1C64F2;
}
</style>

<style>
:root {
  --caret-width: 4px;
  --caret-color: #007bff;
  --caret-color-dim: #7dd3fc;
  --selection-color: rgba(0, 123, 255, 0.3);
}

.textarea-container {
  position: relative;
  width: 100%;
  /* max-width: 500px; */
  @apply bg-white dark:bg-gray-800;
}

.textarea-container {
  /* Shared typography properties are CRITICAL for precise text wrapping alignment */
  .custom-textarea, .text-mirror {
    padding: 16px;
    /*font-size: 18px;*/
    @apply text-sm;
    line-height: 1.5;
    font-family: monospace;
    letter-spacing: 0px;
    box-sizing: border-box;
    width: 100%;
    min-height: 150px;
    /* border: 2px solid #ccc; */
    border-radius: 8px;
    margin: 0;

    /* if you want a seamless look, hide the scrollbars entirely on both elements using CSS (::-webkit-scrollbar { display: none; } and scrollbar-width: none; */
    scrollbar-width: none;
  }
  /* ::-webkit-scrollbar { display: none; } */

  .custom-textarea {
    background: transparent;
    color: transparent; /* Hide native text */
    caret-color: transparent; /* Hide native caret */
    position: relative;
    z-index: 2;
    resize: vertical; /* Allow resizing if needed */
    outline: none;
  }

  .custom-textarea::selection {
    background: transparent;
    color: transparent;
  }
  .custom-textarea::-moz-selection {
    background: transparent;
    color: transparent;
  }

  /* The visual text layer directly underneath */
  .text-mirror {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    box-sizing: border-box;
    /* background: #fff; */
    /* @apply bg-amber-300; */
    @apply bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100;
    z-index: 1;
    pointer-events: none;
    overflow-y: auto;
    /* Essential for exact multi-line text mapping */
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  /* Selection Highlight Styling */
  .custom-selection {
    background-color: rgba(59, 130, 246, 0.35);
    border-radius: 2px;
  }

  /* Custom Caret Container */
  .custom-caret {
    position: relative;
    display: none;
    width: var(--caret-width, 4px);
    height: 1.4em;
    background-color: var(--caret-color);
    vertical-align: middle;
  }

  .custom-textarea.focus-for-keyboard ~ .text-mirror .custom-caret {
    display: inline-block;
    animation: blink 1s step-end infinite;
  }

  .custom-textarea.focus-for-keyboard ~ .text-mirror.has-selection .custom-caret {
    animation: none; /* Stop blinking during active drag selection */
  }

  @keyframes blink {
    from, to { background-color: var(--caret-color) }
    50% { background-color: var(--caret-color-dim) }
  }

  /* Touch Screen Teardrop Handle */
  .touch-teardrop {
    position: absolute;
    bottom: -14px;
    left: 50%;
    width: 16px;
    height: 16px;
    background-color: var(--caret-color);
    border-radius: 0 50% 50% 50%;
    transform: translateX(-50%) rotate(45deg);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    display: none;
  }

  .custom-textarea.focus-for-keyboard ~ .text-mirror .custom-caret .touch-teardrop {
    display: block;
  }

}
</style>